<template>
    <div>
        <div class="form-group name-input">
            <label for="name" class="custom-label">First name and last name *</label>
            <div class="alert">
                <input v-model="name"
                       @input="updateData"
                       @blur="validateName"
                       type="text"
                       id="name"
                       :class="{'error-input': nameError, 'success-input': nameSuccess}"
                       class="custom-input required-element"
                       placeholder="Enter your first name and last name"
                       autocomplete="off"
                       maxlength="30"
                       required>
            </div>
            <div v-if="nameError" class="error-message">{{ nameError }}</div>
        </div>
        <div class="form-group position-input">
            <label for="position" class="custom-label">Position *</label>
            <div class="alert">
                <input v-model="position"
                       @input="updateData"
                       @blur="validatePosition"
                       type="text"
                       id="position"
                       :class="{'error-input': positionError, 'success-input': positionSuccess}"
                       class="custom-input required-element"
                       placeholder="Enter your position"
                       autocomplete="off"
                       maxlength="128"
                       required>
            </div>
            <div v-if="positionError" class="error-message">{{ positionError }}</div>
        </div>
        <div class="form-group email-input">
            <label for="subscrConstrEmail" class="custom-label">Email *</label>
            <div class="alert">
                <input
                    v-model="email"
                    @input="checkEmailLength"
                    @blur="validateEmail"
                    @keydown="preventSpaces"
                    type="email"
                    id="email"
                    :class="{ 'error-input': emailError, 'success-input': emailSuccess }"
                    class="custom-input required-element"
                    placeholder="Enter your email"
                    autocomplete="off"
                    required />
            </div>
            <div v-if="emailError" class="error-message">{{ emailError }}</div>
        </div>
        <div class="form-group phone-input">
            <label for="phone" class="custom-label">Phone Number</label>
            <div class="alert">
                <input v-model="phone"
                       @input="updateData"
                       @blur="validatePhoneNumber"
                       type="tel"
                       id="phone"
                       :class="{'error-input': phoneError,  'success-input': phoneSuccess}"
                       class="custom-input"
                       oninput="this.value = this.value.replace(/[^0-9+-]/g, '');"
                       placeholder="Enter your phone number"
                       autocomplete="off">
            </div>
            <div v-if="phoneError" class="error-message">{{ phoneError }}</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormInputs",
    data() {
        return {
            name: '',
            position: '',
            email: '',
            phone: '',
            nameError: '',
            nameSuccess: '',
            positionError: '',
            positionSuccess: '',
            emailError: '',
            emailSuccess: '',
            phoneError: '',
            phoneSuccess: '',
        }
    },
    computed: {
        maxEmailLength() {
            return this.$options.methods.isMobileDevice() ? 40 : 64;
        },
    },
    methods: {
        updateData() {
            this.$emit('input', {
                name: this.name,
                position: this.position,
                email: this.email,
                phone: this.phone
            })
        },
        validateSpecialCharacters(input) {
            // eslint-disable-next-line no-useless-escape
            const specialCharactersRegex = /[!#$%^&*()=\[\]{};':"\\|<>/?]+/;
            return specialCharactersRegex.test(input);
        },
        validateName() {
            if (this.name === '') {
                this.nameError = 'This field is required';
            } else if (this.validateSpecialCharacters(this.name)) {
                this.nameError = 'Special characters are not allowed';
            } else if (!this.name || !this.name.trim().includes(' ')) {
                this.nameError = 'Enter your first and last name separated by a space.';
                this.nameSuccess = false;
            } else {
                this.nameError = false;
                this.nameSuccess = true;
            }
        },
        validatePosition() {
            if (this.position === '' || !this.position.trim()) {
                this.positionError = 'This field is required';
                this.positionSuccess = '';
                return false;
            }
            if (this.position.length < 3) {
                this.positionError = 'Position must be at least 3 characters long';
                this.positionSuccess = '';
                return false;
            }
            if (this.validateSpecialCharacters(this.position)) {
                this.positionError = 'Special characters are not allowed';
                this.positionSuccess = '';
                return false;
            }
            this.positionError = null;
            this.positionSuccess = 'success-input';
            return true;
        },
        /* ------ Email Validation ------ */
        isMobileDevice() {
            const userAgent = window.navigator.userAgent;
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        },
        validateEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            // eslint-disable-next-line no-useless-escape
            const specialCharactersRegex = /[!#$%^&*()+\-=\[\]{};':"\\|,<>/?]+/;

            if (this.email === '') {
                this.emailError = "This field is required";
                this.emailSuccess = '';
            } else if (specialCharactersRegex.test(this.email)) {
                this.emailError = "Special characters are not allowed";
                this.emailSuccess = '';
            } else if (this.email.length > this.maxEmailLength) {
                this.emailError = `Email must be ${this.maxEmailLength} characters or less`;
                this.emailSuccess = '';
            } else if (!emailRegex.test(this.email)) {
                this.emailError = "Please enter a valid email address";
                this.emailSuccess = '';
            } else {
                this.emailError = '';
                this.emailSuccess = 'success-input';
            }
        },
        // Проверка на ввод в email пробела. Если введен - предотвращаем єто действие.
        preventSpaces(event) {
            if (event.keyCode === 32) {
                event.preventDefault();
            }
        },
        // Проверка на кол-во введенніх символов
        checkEmailLength() {
            if (this.email.length > this.maxEmailLength) {
                this.emailError = `Email must not exceed ${this.maxEmailLength} characters`;
                this.email = this.email.slice(0, this.maxEmailLength);
            } else {
                this.validateEmail();
            }
            this.updateData();
        },
        validatePhoneNumber() {
            const phoneRegex = /^\+?\d{1,3}[- ]?\(?\d{2,4}\)?[- ]?\d{3}[- ]?\d{2}[- ]?\d{2}$/;
            if (this.phone === '') {
                this.phoneError = '';
                this.phoneSuccess = 'success-input';
                return false;
            } else if (!phoneRegex.test(this.phone)) {
                this.phoneError = 'Invalid phone number';
                this.phoneSuccess = '';
            } else {
                this.phoneError = '';
                this.phoneSuccess = 'success-input';
            }
        },
        // getUserInfo() {
        //     const userInfo = localStorage.getItem("user_info");
        //     return userInfo ? JSON.parse(userInfo) : null;
        // },
        updateUserInfo() {
            if (this.$root.user) {
                this.name = this.$root.user.given_name + ' ' + this.$root.user.family_name || '';
                this.email = this.$root.user.email || '';

                this.updateData();

                // Вызываем методы валидации после автозаполнения
                this.validateEmail();
            }
            // const userInfo = this.getUserInfo();
            // if (userInfo) {
            //     this.email = userInfo.email || "";

            //     this.updateData();

            //     // Вызываем методы валидации после автозаполнения
            //     this.validateEmail();
            // }
        },
        clearForm() {
            this.name = "";
            this.email = "";
            this.position = "";
            this.phone = "";
        },
    },
    mounted() {
        this.updateUserInfo();
        this.$root.$on("logged-out", this.clearForm);
        this.$root.$on("logged-in", this.updateUserInfo);
    },
    beforeDestroy() {
        this.$root.$off("logged-out", this.clearForm);
        this.$root.$off("logged-in", this.updateUserInfo);
    },
}
</script>

<style scoped>
.error-input {
    border: 1px solid #FF424FFF !important;
}

.error-message {
    width: 100%;
    margin-top: 8px;
    line-height: 1.57;
    font-size: 14px;
    text-align: left;
    color: #FF424FFF;
}
</style>